/**
 * Favorite Views ("SmartViews") — a named leads-filter bundle that becomes its
 * own sidebar route.
 *
 * The server stores `filters` as opaque JSON, so this file is the ONLY
 * definition of the saved shape. `version` exists so a future change can
 * migrate old payloads instead of silently mis-reading them.
 */
import type { FilterState } from "@/features/leads/components/leadFilter/context/FilterContext";

/** Which date range a stored preset token belongs to. */
export type DatePresetSlot =
  | "date"
  | "followup"
  | "trackFollowup"
  | "call"
  | "lastContacted"
  | "activity";

export interface FavoriteViewPayload {
  version: 1;
  /**
   * Verbatim `appliedFilters` — already diffed against `filterFieldDefaults`
   * by `applyFilters()`, so it only carries non-default values.
   */
  filters: Record<string, any>;
  search?: string;
  viewMode?: "Grid" | "List" | "Board";
  /**
   * Relative-date tokens, re-resolved on open so "Last 7 Days" stays current
   * instead of freezing to the dates it was saved with. Absent = the range was
   * absolute and is restored as-is from `filters`.
   */
  datePresets?: Partial<Record<DatePresetSlot, string>>;
  /**
   * The view's column layout (Show/hide fields), saved WITH the view so it
   * reads the same for everyone it is shared with. See
   * `utils/viewColumns.ts`. Absent on views saved before columns travelled
   * with the view — those leave the opener's layout alone.
   */
  columns?: Record<string, boolean | Record<string, boolean>>;
  /**
   * THE STAGE THE VIEW WAS SAVED FROM.
   *
   * A pipeline stage route is not "the leads list with a stage chip" — it is
   * its own chain (child-aware placement, the stage's date-type default, an
   * entry target pinned to that stage, stage-scoped deals on the card, and the
   * ordering that follows). Saving from /Offer Letter (COL) and dropping the
   * stage gave a view with the same chips but a different list: differently
   * ordered, dated by `createdAt`, counting deals from every other stage.
   *
   * Deliberately NOT inside `filters`: `applyDynamicFilters` rebuilds
   * `appliedFilters` from the known `FilterState` keys only, so an unknown key
   * parked there would be silently dropped the first time the user pressed
   * Apply and the view would un-pin itself mid-session. The stage is the
   * view's IDENTITY, not one of its chips — which is also why it has no
   * dropdown of its own, exactly like the stage route it came from.
   *
   * Absent = a view saved from /leads (or before this existed): the plain
   * leads list, unchanged.
   */
  stageId?: number;
}

export interface FavoriteView {
  id: number;
  name: string;
  /** Lucide icon name — same contract as `Stage.iconName`. */
  iconName: string;
  filters: FavoriteViewPayload;
  isShared: boolean;
  order: number;
  isActive: boolean;
  userId: number;
  createdById: number | null;
  createdAt: string;
  updatedAt: string;
}

export type FavoriteViewSeed = Partial<FilterState> & {
  appliedFilters?: Record<string, any>;
};

/** The sidebar/route key for a view — mirrors the `stage-<id>` convention. */
export const viewRouteKey = (viewId: number | string) => `view-${viewId}`;

/** Redux route key for the per-route dynamic filter slice. */
export const viewRouteId = (viewId: number | string) => `view-${viewId}`;

/** `view-12` → 12; anything else → null. */
export function parseViewRouteId(routeId: unknown): number | null {
  const match = /^view-(\d+)$/.exec(String(routeId ?? ""));
  return match ? Number(match[1]) : null;
}
